import { useEffect, useMemo, useState } from 'react';
import { merge } from 'ts-deepmerge';
import { alpha, lighten } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import highchartsMore from 'highcharts/highcharts-more';
import Accessibility from 'highcharts/modules/accessibility';
import useBundleTranslation from 'i18n';
import { useBlendColors } from 'hooks/useDesign';
highchartsMore(Highcharts);
Accessibility(Highcharts);

export { Highcharts };

(function (H) {
    H.wrap(H.Legend.prototype, 'handleOverflow', function (p, h) {
        return h;
    });
})(Highcharts);

Highcharts.dateFormats = {
    //@ts-ignore
    q: function (timestamp) {
        const date = new Date(timestamp);
        let quarter = 0;

        if (date) {
            quarter = Math.ceil((date.getUTCMonth() + 1) / 3);
        }

        return quarter;
    },
};

export default function Chart({
    chartOptions,
    afterChartCreated,
    constructorType,
    immutable,
}: {
    chartOptions: Highcharts.Options;
    afterChartCreated?: Highcharts.ChartCallbackFunction;
    constructorType?: 'mapChart' | 'stockChart' | 'chart';
    immutable?: boolean;
}) {
    const { t } = useBundleTranslation();
    const appTheme = useTheme();
    const [languageReady, setLanguageReady] = useState(false);
    useEffect(() => {
        Highcharts.setOptions({
            lang: {
                months: [
                    t('January'),
                    t('February'),
                    t('March'),
                    t('April'),
                    t('May'),
                    t('June'),
                    t('July'),
                    t('August'),
                    t('September'),
                    t('October'),
                    t('November'),
                    t('December'),
                ],
                shortMonths: [
                    t('Jan'),
                    t('Feb'),
                    t('Mar'),
                    t('Apr'),
                    t('May'),
                    t('Jun'),
                    t('Jul'),
                    t('Aug'),
                    t('Sep'),
                    t('Oct'),
                    t('Nov'),
                    t('Dec'),
                ],
                weekdays: [
                    t('Sunday'),
                    t('Monday'),
                    t('Tuesday'),
                    t('Wednesday'),
                    t('Thursday'),
                    t('Friday'),
                    t('Saturday'),
                ],
            },
        });
        setLanguageReady(true);
    }, []);

    const themeSettings = {
        chart: {
            plotBorderColor: useBlendColors(alpha(appTheme.palette.text.primary, 0.08)),
        },
        xyAxis: {
            gridLineColor: useBlendColors(alpha(appTheme.palette.text.primary, 0.16)),
            titleColor: useBlendColors(alpha(appTheme.palette.text.primary, 0.4)),
            labelColor: useBlendColors(alpha(appTheme.palette.text.primary, 0.64)),
        },
        legend: {
            itemStyle: {
                color: appTheme.palette.primary.main,
            },
            itemHoverStyle: {
                color: lighten(appTheme.palette.primary.main, 0.24),
            },
            itemHiddenStyle: {
                color: useBlendColors(alpha(appTheme.palette.text.primary, 0.64)),
            },
        },
    };

    const defaultOptions = {
        chart: {
            plotBorderColor: themeSettings.chart.plotBorderColor,
            backgroundColor: 'transparent',
            style: {
                fontFamily: 'Arial, helvetica, sans-serif',
            },
        },
        navigator: {
            outlineColor: themeSettings.xyAxis.gridLineColor,
            xAxis: {
                gridLineColor: themeSettings.xyAxis.gridLineColor,
                tickColor: themeSettings.xyAxis.gridLineColor,
                lineColor: themeSettings.xyAxis.gridLineColor,
            },
        },
        legend: {
            itemStyle: {
                font: '13px Arial, helvetica, sans-serif',
                color: themeSettings.legend.itemStyle.color,
            },
            itemHoverStyle: {
                color: themeSettings.legend.itemHoverStyle.color,
            },
            itemHiddenStyle: {
                color: themeSettings.legend.itemHiddenStyle.color,
            },
        },
        rangeSelector: {
            inputBoxHeight: 20,
            inputBoxWidth: 100,
            inputBoxBorderColor: alpha(appTheme.palette.text.primary, 0.08),
            inputStyle: {
                color: appTheme.palette.primary.main,
            },
            labelStyle: {
                color: themeSettings.xyAxis.labelColor,
            },
            buttonTheme: {
                fill: useBlendColors(alpha(appTheme.palette.text.primary, 0.16)),
                stroke: 'none',
                style: {
                    color: useBlendColors(alpha(appTheme.palette.text.primary, 0.64)),
                },
                states: {
                    hover: {
                        fill: useBlendColors(alpha(appTheme.palette.text.primary, 0.08)),
                    },
                    select: {
                        fill: useBlendColors(alpha(appTheme.palette.primary.main, 0.08)),
                        style: {
                            color: appTheme.palette.primary.main,
                        },
                    },
                    disabled: {
                        fill: useBlendColors(alpha(appTheme.palette.text.primary, 0.04)),
                        style: {
                            color: useBlendColors(alpha(appTheme.palette.text.primary, 0.16)),
                        },
                    },
                },
            },
        },
        xAxis: {
            gridLineColor: themeSettings.xyAxis.gridLineColor,
            tickColor: themeSettings.xyAxis.gridLineColor,
            lineColor: themeSettings.xyAxis.gridLineColor,
            labels: {
                style: {
                    color: themeSettings.xyAxis.labelColor,
                },
            },
        },
        yAxis: {
            gridLineColor: themeSettings.xyAxis.gridLineColor,
            tickColor: themeSettings.xyAxis.gridLineColor,
            lineColor: themeSettings.xyAxis.gridLineColor,
            labels: {
                style: {
                    color: themeSettings.xyAxis.labelColor,
                },
            },
            title: {
                style: {
                    color: themeSettings.xyAxis.titleColor,
                },
            },
        },
        tooltip: {
            padding: 10,
            borderRadius: 5,
            backgroundColor: appTheme.palette.background.default,
            borderColor: 'none',
            style: {
                color: appTheme.palette.text.primary,
            },
        },
        navigation: {
            buttonOptions: {
                symbolFill: appTheme.palette.text.primary,
                symbolStroke: appTheme.palette.text.primary,
                theme: {
                    fill: appTheme.palette.background.default,
                },
            },
        },
        mapNavigation: {
            buttonOptions: {
                style: {
                    color: useBlendColors(alpha(appTheme.palette.text.primary, 0.64)),
                },
                theme: {
                    fill: appTheme.palette.background.default,
                    stroke: useBlendColors(alpha(appTheme.palette.text.primary, 0.16)),

                    states: {
                        hover: {
                            fill: useBlendColors(alpha(appTheme.palette.text.primary, 0.16)),
                        },
                    },
                },
            },
        },
    };

    const options = useMemo(() => merge(defaultOptions, chartOptions), [chartOptions]);

    if (!languageReady) {
        return null;
    }

    return (
        <HighchartsReact
            callback={afterChartCreated}
            constructorType={constructorType ?? 'stockChart'}
            highcharts={Highcharts}
            options={options}
            immutable={immutable}
        />
    );
}
